import { type FormEvent, type ReactNode } from 'react';
import { CheckCircle2, RefreshCcw, Save, Send } from 'lucide-react';
import { Badge, Button, EmptyState, Input, Spinner } from '@helios/ui';
import { Link } from 'react-router';
import { useUserPreferences, USER_DATE_FORMATS, formatPreferenceDate, DEFAULT_USER_PREFERENCES } from './UserPreferencesProvider';
import type {
  FeatureFlagItem,
  NotificationItem,
  OrganizationSettings,
  UserSettings,
} from '@/services/settingsClient';
import {
  SettingsField,
  SettingsSection,
  SettingsTable,
  SettingsTableRow,
  SettingsTd,
  SettingsToggle,
  settingsControlClass,
} from '@/features/settings/settingsChrome';

export type UserForm = UserSettings['preferences'] & {
  name: string;
  title: string;
};

export type OrganizationForm = OrganizationSettings['settings'] & {
  name: string;
  type: string;
};

export const selectClassName = settingsControlClass;

export {
  SettingsLoadingState,
  SettingsMutationBanner,
  SettingsField as Field,
  SettingsToggle as Toggle,
} from '@/features/settings/settingsChrome';

export function UserPreferencesForm({
  form,
  onChange,
  onSubmit,
  saving,
  dirty = true,
  landingPages = [],
}: {
  form: UserForm | null;
  onChange: (form: UserForm) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  saving: boolean;
  dirty?: boolean;
  landingPages?: Array<{ path: string; label: string }>;
}) {
  if (!form) {
    return (
      <SettingsSection title="User preferences">
        <EmptyState
          title="User preferences"
          description="No current user settings were returned."
        />
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      title="Profile"
      description="Your profile and workspace display settings. Changes apply to your signed-in workspace."
      actions={
        <Button type="submit" form="settings-preferences-form" size="xs" disabled={saving || !dirty}>
          {saving ? <Spinner className="h-3.5 w-3.5" aria-hidden="true" /> : <Save className="h-3.5 w-3.5" aria-hidden="true" />}
          Save preferences
        </Button>
      }
    >
      <form id="settings-preferences-form" className="grid gap-2 sm:grid-cols-2" onSubmit={onSubmit}>
        <SettingsField label="Name">
          <Input className="h-[var(--helios-control-height,28px)] rounded-md" value={form.name} onChange={(event) => onChange({ ...form, name: event.target.value })} />
        </SettingsField>
        <SettingsField label="Title">
          <Input className="h-[var(--helios-control-height,28px)] rounded-md" value={form.title} onChange={(event) => onChange({ ...form, title: event.target.value })} />
        </SettingsField>
        <SettingsField label="Timezone">
          <Input className="h-[var(--helios-control-height,28px)] rounded-md" value={form.timezone} onChange={(event) => onChange({ ...form, timezone: event.target.value })} />
        </SettingsField>
        <SettingsField label="Date format">
          <select className={settingsControlClass} value={form.dateFormat} onChange={(event) => onChange({ ...form, dateFormat: event.target.value })}>
            {!USER_DATE_FORMATS.some((format) => format === form.dateFormat) ? <option value={form.dateFormat}>{form.dateFormat} (stored format)</option> : null}
            {USER_DATE_FORMATS.map((format) => <option key={format} value={format}>{format} · {formatPreferenceDate('2026-10-07', { ...DEFAULT_USER_PREFERENCES, dateFormat: format })}</option>)}
          </select>
        </SettingsField>
        <SettingsField label="Landing page">
          <select className={settingsControlClass} value={form.landingPage} onChange={(event) => onChange({ ...form, landingPage: event.target.value })}>
            {!landingPages.some((item) => item.path === form.landingPage) ? <option value={form.landingPage}>{form.landingPage} (stored; unavailable with current access)</option> : null}
            {landingPages.map((item) => <option key={item.path} value={item.path}>{item.label}</option>)}
          </select>
        </SettingsField>
        <div className="grid gap-1.5">
          <SettingsToggle label="Dense mode" checked={form.denseMode} onChange={(checked) => onChange({ ...form, denseMode: checked })} />
          <SettingsToggle
            label="Email digest"
            checked={form.emailDigestEnabled}
            onChange={() => undefined}
            disabled
          />
          <SettingsToggle
            label="Critical only"
            checked={form.criticalOnlyNotifications}
            onChange={(checked) => onChange({ ...form, criticalOnlyNotifications: checked })}
          />
        </div>
        <p className="text-2xs text-muted-foreground sm:col-span-2">Email digest delivery is unavailable. The stored preference is shown for reference. Critical only filters supported advisor alerts; direct notifications still reach your inbox.</p>
      </form>
    </SettingsSection>
  );
}

export function OrganizationSettingsForm({
  form,
  onChange,
  onSubmit,
  saving,
  dirty = true,
}: {
  form: OrganizationForm | null;
  onChange: (form: OrganizationForm) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  saving: boolean;
  dirty?: boolean;
}) {
  if (!form) {
    return (
      <SettingsSection title="Company">
        <EmptyState title="Organization settings" description="No tenant organization was returned." />
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      id="company"
      title="Company"
      description="Tenant organization row. Access policy is edited on Access control."
      actions={
        <Button type="submit" form="settings-organization-form" size="xs" disabled={saving || !dirty}>
          {saving ? <Spinner className="h-3.5 w-3.5" aria-hidden="true" /> : <Save className="h-3.5 w-3.5" aria-hidden="true" />}
          Save organization
        </Button>
      }
    >
      <form id="settings-organization-form" className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3" onSubmit={onSubmit}>
        <SettingsField label="Company name">
          <Input className="h-[var(--helios-control-height,28px)] rounded-md" value={form.name} onChange={(event) => onChange({ ...form, name: event.target.value })} />
        </SettingsField>
        <SettingsField label="Organization type">
          <Input className="h-[var(--helios-control-height,28px)] rounded-md" value={form.type} onChange={(event) => onChange({ ...form, type: event.target.value })} />
        </SettingsField>
        <SettingsField label="Timezone">
          <Input className="h-[var(--helios-control-height,28px)] rounded-md" value={form.timezone} onChange={(event) => onChange({ ...form, timezone: event.target.value })} />
        </SettingsField>
        <SettingsField label="Fiscal year start">
          <Input
            className="h-[var(--helios-control-height,28px)] rounded-md"
            type="number"
            min={1}
            max={12}
            value={form.fiscalYearStartMonth}
            onChange={(event) => onChange({ ...form, fiscalYearStartMonth: Number(event.target.value) })}
          />
        </SettingsField>
        <SettingsField label="Audit retention years">
          <Input
            className="h-[var(--helios-control-height,28px)] rounded-md"
            type="number"
            min={1}
            max={25}
            value={form.auditRetentionYears}
            onChange={(event) => onChange({ ...form, auditRetentionYears: Number(event.target.value) })}
          />
        </SettingsField>
        <p className="text-2xs text-muted-foreground sm:col-span-2 xl:col-span-3">Audit retention is a stored policy preference; it does not configure an automated deletion schedule. New retention values must be 7–25 years.</p>
      </form>
    </SettingsSection>
  );
}

export function FeatureFlagPanel({
  flags,
  rollouts,
  onRolloutChange,
  onToggle,
  onSaveRollout,
  pendingKey,
  saving,
  canManage = false,
  onRevert,
  dirtyKeys = [],
}: {
  flags: FeatureFlagItem[];
  rollouts: Record<string, number>;
  onRolloutChange: (key: string, value: number) => void;
  onToggle: (flag: FeatureFlagItem) => void;
  onSaveRollout: (flag: FeatureFlagItem) => void;
  pendingKey: string | null;
  saving: boolean;
  canManage?: boolean;
  onRevert?: (flag: FeatureFlagItem) => void;
  dirtyKeys?: string[];
}) {
  return (
    <SettingsSection title="Feature flags" description="Tenant-scoped rollout. Locked flags stay read-only.">
      {flags.length === 0 ? (
        <EmptyState title="No feature flags" description="Seed S03 data to populate tenant feature flags." />
      ) : (
        <SettingsTable columns={['Flag', 'Category', 'Rollout', 'Status', 'Actions']} minWidth={720}>
          {flags.map((flag) => {
            const rowSaving = saving && pendingKey === flag.key;
            return (
              <SettingsTableRow key={flag.id}>
                <SettingsTd>
                  <p className="font-medium text-foreground">{flag.name}</p>
                  <p className="text-2xs text-muted-foreground">{flag.description ?? flag.key}</p>
                </SettingsTd>
                <SettingsTd className="text-muted-foreground">{flag.category}</SettingsTd>
                <SettingsTd>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={rollouts[flag.key] ?? flag.rolloutPercent}
                    aria-label={`Rollout for ${flag.name}`}
                    disabled={!canManage || flag.locked || saving}
                    onChange={(event) => onRolloutChange(flag.key, Number(event.target.value))}
                    endAdornment={<span className="text-2xs">%</span>}
                    className="h-[var(--helios-control-height,28px)] w-24 rounded-md"
                  />
                </SettingsTd>
                <SettingsTd>
                  <Badge variant={flag.enabled ? 'success' : 'outline'}>
                    {flag.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  {flag.locked ? (
                    <Badge variant="warning" className="ml-1">
                      Locked
                    </Badge>
                  ) : null}
                </SettingsTd>
                <SettingsTd>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      disabled={!canManage || flag.locked || saving}
                      onClick={() => onToggle(flag)}
                    >
                      {rowSaving ? <Spinner className="h-3 w-3" aria-hidden="true" /> : null}
                      {flag.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      type="button"
                      size="xs"
                      variant="ghost"
                      disabled={!canManage || flag.locked || saving || rollouts[flag.key] === flag.rolloutPercent}
                      onClick={() => onSaveRollout(flag)}
                    >
                      Save rollout
                    </Button>
                    {onRevert && dirtyKeys.includes(flag.key) ? <Button type="button" size="xs" variant="ghost" disabled={saving} onClick={() => onRevert(flag)}>Revert {flag.name}</Button> : null}
                  </div>
                </SettingsTd>
              </SettingsTableRow>
            );
          })}
        </SettingsTable>
      )}
    </SettingsSection>
  );
}

export function NotificationsPanel({
  items,
  loading,
  onRefresh,
  onMarkAllRead,
  onCreateTest,
  onMarkRead,
  busy,
  canUpdate = false,
}: {
  items: NotificationItem[];
  loading: boolean;
  onRefresh: () => void;
  onMarkAllRead: () => void;
  onCreateTest: () => void;
  onMarkRead: (id: string) => void;
  busy: boolean;
  canUpdate?: boolean;
}) {
  const { formatDateTime } = useUserPreferences();
  return (
    <SettingsSection
      id="notifications"
      title="Inbox"
      description="Operator alerts for this user. Audit events stay on the Audit log."
      actions={
        <>
          {canUpdate ? <Button type="button" size="xs" variant="outline" disabled={busy} onClick={onCreateTest}>
            <Send className="h-3 w-3" aria-hidden="true" />
            Test
          </Button> : null}
          {canUpdate ? <Button type="button" size="xs" variant="ghost" disabled={busy} onClick={onMarkAllRead}>
            <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
            Mark all read
          </Button> : null}
          <Button type="button" size="xs" variant="ghost" onClick={onRefresh}>
            <RefreshCcw className="h-3 w-3" aria-hidden="true" />
            Refresh
          </Button>
        </>
      }
    >
      {loading ? (
        <LoadingRow label="Loading notifications…" />
      ) : items.length === 0 ? (
        <EmptyState
          title="No notifications"
          description="Approvals, status changes, and advisor alerts from across HELIOS appear here."
        />
      ) : (
        <SettingsTable columns={['Alert', 'When', '']} minWidth={560}>
          {items.map((item) => (
            <SettingsTableRow key={item.id}>
              <SettingsTd>
                <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
                  <Badge variant={notificationVariant(item.type)}>{formatNotificationType(item.type)}</Badge>
                  {item.isRead ? <Badge variant="outline">Read</Badge> : null}
                </div>
                <p className="font-medium text-foreground">{item.title}</p>
                <p className="text-2xs text-muted-foreground">{item.body}</p>
              </SettingsTd>
              <SettingsTd className="whitespace-nowrap text-muted-foreground">{formatDateTime(item.createdAt)}</SettingsTd>
              <SettingsTd>
                <div className="flex flex-wrap gap-1">
                {safeNotificationUrl(item.actionUrl) ? <Button size="xs" variant="outline" asChild><Link to={safeNotificationUrl(item.actionUrl)!}>Open</Link></Button> : null}
                {canUpdate ? <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  disabled={item.isRead || busy}
                  onClick={() => onMarkRead(item.id)}
                >
                  Mark read
                </Button> : null}
                </div>
              </SettingsTd>
            </SettingsTableRow>
          ))}
        </SettingsTable>
      )}
    </SettingsSection>
  );
}

export function AuditTrailPanel({
  items,
  loading,
  page,
  totalPages,
  canPrevious,
  canNext,
  onPrevious,
  onNext,
  onRefresh,
  filters,
  error = false,
}: {
  items: Array<{
    id: string;
    action: string;
    entityType: string;
    actor: { name: string; email: string } | null;
    requestId: string | null;
    createdAt: string;
  }>;
  loading: boolean;
  page: number;
  totalPages: number;
  canPrevious: boolean;
  canNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onRefresh: () => void;
  filters?: ReactNode;
  error?: boolean;
}) {
  const { formatDateTime } = useUserPreferences();
  return (
    <SettingsSection
      title="Audit log"
      description="Who changed what, and when. Tenant-scoped unless you are a platform admin."
      actions={
        <Button type="button" size="xs" variant="ghost" onClick={onRefresh}>
          <RefreshCcw className="h-3 w-3" aria-hidden="true" />
          Refresh
        </Button>
      }
    >
      {filters ? <div className="mb-2">{filters}</div> : null}
      {error ? null : loading ? (
        <LoadingRow label="Loading audit trail…" />
      ) : items.length === 0 ? (
        <EmptyState
          title="No audit events"
          description="Settings and notification mutations will appear here after they run."
        />
      ) : (
        <SettingsTable columns={['Action', 'Actor', 'Entity', 'Time']} minWidth={640}>
          {items.map((item) => (
            <SettingsTableRow key={item.id}>
              <SettingsTd>
                <p className="font-medium text-foreground">{item.action}</p>
                <p className="text-2xs text-muted-foreground">{item.requestId ?? 'No request id'}</p>
              </SettingsTd>
              <SettingsTd className="text-muted-foreground">
                {item.actor ? `${item.actor.name} (${item.actor.email})` : 'System'}
              </SettingsTd>
              <SettingsTd className="text-muted-foreground">{item.entityType}</SettingsTd>
              <SettingsTd className="text-muted-foreground">{formatDateTime(item.createdAt)}</SettingsTd>
            </SettingsTableRow>
          ))}
        </SettingsTable>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-2xs text-muted-foreground">
          Page {page}
          {totalPages > 0 ? ` of ${totalPages}` : ''}
        </span>
        <div className="flex gap-1">
          <Button type="button" size="xs" variant="outline" disabled={!canPrevious} onClick={onPrevious}>
            Previous
          </Button>
          <Button type="button" size="xs" variant="outline" disabled={!canNext} onClick={onNext}>
            Next
          </Button>
        </div>
      </div>
    </SettingsSection>
  );
}

export function SettingsCardEmpty({ title, description }: { title: string; description: string }) {
  return (
    <SettingsSection title={title}>
      <EmptyState title={title} description={description} />
    </SettingsSection>
  );
}

export function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-background/40 px-3 py-2 text-xs text-muted-foreground">
      <Spinner className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
      {label}
    </div>
  );
}

export function toUserForm(user: UserSettings): UserForm {
  return {
    name: user.name,
    title: user.title ?? '',
    ...user.preferences,
  };
}

export function toOrganizationForm(organization: OrganizationSettings): OrganizationForm {
  return {
    name: organization.name,
    type: organization.type ?? '',
    ...organization.settings,
  };
}

export function clampRollout(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

export function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function notificationVariant(type: NotificationItem['type']) {
  if (type === 'CRITICAL') return 'critical';
  if (type === 'WARNING') return 'warning';
  if (type === 'SUCCESS') return 'success';
  return 'info';
}

export function safeNotificationUrl(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || /[\\\s]/.test(value)
    || [...value].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return null;
  return value;
}

function formatNotificationType(type: NotificationItem['type']): string {
  return type.toLowerCase().replace(/^\w/, (char) => char.toUpperCase());
}
